import React, { useEffect } from "react";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import TrioBallsIcon from "../../../../../../images/common/ball-trio.svg";
import DonationIcon from "../../../../../../images/donation/donating-hands.svg";
import ServiceIcon from "../../../../../../images/common/service.svg";
import ProductIcon from "../../../../../../images/common/product.svg";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import ActiveLinkIcon from "../../../../../../images/sidebar/link-royal-blue.svg";
import DeleteCheckoutPopup from "./DeleteCheckoutPopup";
import {  CheckOutService } from "../../../shared/services";
import {
  ActiveInactiveButton,
  AuthService,
  CustomLabel,
  FlexContainer,
  ITableHeaders,
  TableHeader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { CheckoutGridModel } from "../../../shared/models";
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ItemContentArea,
  TableGrid,
} from "../../../shared/components";
import { GridNames, ItemDropDownOptions } from "../../../shared/enums";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import { debounceTime, Subscription } from "rxjs";

type IProps = {
  navigate: NavigateFunction;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

const CheckoutGrid: React.FC<IProps> = ({
  itemStore,
  navigate,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [deleteCheckout, setDeleteCheckout] =
    React.useState<CheckoutGridModel | null>(null);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(true);

  useEffect(() => {
    itemStore.updateSearchValue("", ItemDropDownOptions.Checkout);
    loadData(
      itemStore.checkoutGridPagination.pageNumber,
      itemStore.checkoutGridPagination.pageSize,
      itemStore.checkoutSearchValue,
      true
    );

    const subscription: Subscription = itemStore.checkoutSearchSubject
      .pipe(debounceTime(500))
      .subscribe(() => {
        loadData(
          itemStore.checkoutGridPagination.pageNumber,
          itemStore.checkoutGridPagination.pageSize,
          itemStore.checkoutSearchValue
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  function onEdit(id: string) {
    if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
    navigate(`/app/checkout/detail/${id}`);
  }

  function toggleDeletePopup(
    checkout: CheckoutGridModel | null,
    isDeleted: boolean
  ) {
    setDeleteCheckout(checkout);
    if (isDeleted) {
      loadData(
        itemStore.checkoutGridPagination.pageNumber,
        itemStore.checkoutGridPagination.pageSize,
        ""
      );
    }
  }

  const onPaginationChange = (pageNumber: number, pageSize: number) => {
    loadData(pageNumber, pageSize, itemStore.checkoutSearchValue);
  };

  function loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    CheckOutService.getGridData(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (result) => {
        itemStore.deserializeCheckoutGridData(result.result || [], result.meta);
        setShowLoader(false);
      },
      error: (error) => {
        console.error(error);
        setShowLoader(false);
      },
    });
  }

  const handleCopyClick = (url: string) => {
    const fullURL = url;
    navigator.clipboard
      .writeText(fullURL)
      .then(() => {
        ToastService.showSuccess("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  function columns() {
    const columns: ITableHeaders<CheckoutGridModel>[] = [
      {
        dataKey: "isActive",
        title: "Status",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: CheckoutGridModel) => {
          return (
            <ActiveInactiveButton minWidth="80px" inActive={!data.isActive}>
              {data.isActive ? "Active" : "Inactive"}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "name",
        title: "Name",
        cellColor: "#556bb8",
        className: "table-col-25",
        cellClassName: "table-col-25",
        onCellClick: (e, data: CheckoutGridModel) => {
          goToDetailPage(data.id, data.name);
        },
        onCellRender: (data: CheckoutGridModel) => {
          return <CustomLabel fontSize={"14px"}>{data.name}</CustomLabel>;
        },
      },
      {
        dataKey: "link",
        title: "Link / URL",
        className: "table-col-15",
        cellClassName: "table-col-15",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: CheckoutGridModel) => {
          return data.link ? (
            <a href={data.link} target="blank" className="link-text">
              Checkout Link
            </a>
          ) : (
            ""
          );
        },
      },
      {
        dataKey: "items",
        title: "Items",
        className: "table-col-10",
        cellClassName: "table-col-10",
        cellColor: "#556bb8",
        onCellRender: (data: CheckoutGridModel) => {
          const itemsTypes = data.items?.map((item) => item.type) || [];
          const uniqueItems = itemsTypes.filter(UtilityMethods.onlyUnique);
          return (
            <FlexContainer
              spacing={10}
              onClick={() => goToDetailPage(data.id, data.name)}
            >
              {uniqueItems.map((itemType) => {
                switch (itemType) {
                  case "Service":
                    return (
                      <img
                        width={18}
                        height={18}
                        src={ServiceIcon}
                        alt="Service"
                      />
                    );
                  case "Product":
                    return (
                      <img
                        width={18}
                        height={18}
                        src={ProductIcon}
                        alt="Product"
                      />
                    );
                  case "Membership":
                    return (
                      <img
                        width={18}
                        height={18}
                        src={TrioBallsIcon}
                        alt="Membership"
                      />
                    );
                  case "Donation":
                    return (
                      <img
                        width={18}
                        height={18}
                        src={DonationIcon}
                        alt="Donation"
                      />
                    );
                  default:
                    return null;
                }
              })}
            </FlexContainer>
          );
        },
      },
      {
        dataKey: "totalPurchases",
        title: "Number",
        cellColor: "#556bb8",
        className: "table-col-10 text-center-pr-30",
        cellClassName: "table-col-10 text-center-pr-30",
        onCellRender: (data: CheckoutGridModel) => {
          return (
            <CustomLabel fontSize={"14px"}>
              {UtilityMethods.numberWithCommas(data.totalPurchases)}
            </CustomLabel>
          );
        },
      },
      {
        dataKey: "totalAmount",
        title: "Total",
        cellColor: "#556bb8",
        className: "table-col-10 text-right-pr-30",
        cellClassName: "table-col-10 text-right-pr-30",
        onCellRender: (data: CheckoutGridModel) => {
          return (
            <>
              <CustomLabel fontSize={"14px"}>
                {"$" + AccountSettingsStore.formatAmount(data.totalAmount)}
              </CustomLabel>
            </>
          );
        },
      },
      {
        dataKey: "lastPurchase",
        title: "Last",
        className: "table-col-10 text-center",
        cellClassName: "table-col-10 text-center",

        onCellRender: (data: CheckoutGridModel) => {
          return (
            <CustomLabel fontSize={"14px"}>
              {data.lastPurchase &&
                UtilityMethods.formatMMDDYYYY(data.lastPurchase)}
            </CustomLabel>
          );
        },
      },
      {
        dataKey: "lastPurchase",
        title: "Actions",
        className: "table-col-10 text-center",
        cellClassName: "table-col-10 text-center",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: CheckoutGridModel) => {
          return (
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={10}
            >
              <img
                src={ActiveLinkIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyClick(data.link);
                }}
                className="img-15 clickable"
                alt="Copy Link"
              />
              <img
                src={EditIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(data.id);
                }}
                className="img-15 clickable"
                alt="Edit"
              />
              <img
                src={DeleteIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!rolesAndPermissionStore.hasManagePermission("Delete"))
                    return;
                  toggleDeletePopup(data, false);
                }}
                className="img-15 clickable"
                alt="Delete"
              />
            </FlexContainer>
          );
        },
      },
    ];
    return columns;
  }

  const goToDetailPage = (id: string, name: string) => {
    navigate(`/app/checkouts/detail/${id}/${encodeURIComponent(name)}`);
  };

  const isNoData =
    firstTimeLoad &&
    itemStore.checkoutGridPagination.totalItemCount === 0 &&
    !showLoader &&
    itemStore.checkoutSearchValue === "";

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={itemStore}
        showSearchInput={!isNoData}
        showButton={!isNoData}
      />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{showLoader && firstTimeLoad ? null : content}</>}
          merchantAccountStore={merchantAccountStore}
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showLoader}
          //className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  return renderDetails(
    <>
      {deleteCheckout && (
        <DeleteCheckoutPopup
          onClose={(isDeleted) => toggleDeletePopup(null, isDeleted)}
          id={deleteCheckout.id}
        />
      )}
      <TableGrid
        tableHeader={
          <TableHeader
            showFilter={itemStore.showItemsFilter}
            onToggleFilter={() => itemStore.toggleItemsFilter()}
            total={itemStore.checkoutGridPagination.totalItemCount}
          />
        }
        totalItemCount={itemStore.checkoutGridPagination.totalItemCount}
        cellSpacing={20}
        onPaginationChange={(pageNumber: number, pageSize: number) =>
          onPaginationChange(pageNumber, pageSize)
        }
        pagination={itemStore.checkoutGridPagination}
        data={itemStore.checkoutGridData}
        countriesWithState={itemStore.countryStore.countriesWithState}
        headers={columns()}
        onRowClick={(data: CheckoutGridModel) =>
          goToDetailPage(data.id, data.name)
        }
        height={
          itemStore.showItemsFilter
            ? "calc(100vh - 499px)"
            : merchantAccountStore.hasNotificationBar()
              ? "calc(100vh - 350px)"
              : ""
        }
        rolesAndPermissionStore={rolesAndPermissionStore}
        name={GridNames.Checkout}
        hideTable={isNoData}
      />
    </>
  );
};

export default withRouter(observer(CheckoutGrid));
